import { useEffect, useRef, useState } from "react";
import EditCustomerModal from "./EditCustomerModal";
import usePosOrders from "../../../services/Pos/usePosOrders";
import CancelOrderModal from "./CancelOrderModal";
import { toast } from "react-toastify";
import POSPrint from "./PosPrint";
import useProfileDashboard from "../../../services/Settings/useProfileDashboard";

export default function PosOrderDetailsModal({
  open,
  onClose,
  order,
  onEdit,
  onSettle,
  onMarkAsComplete,
  markingComplete = false,
  reciptDetails: restaurantReceiptDetails,
}) {
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { cancelPosOrder, getOrders } = usePosOrders();
  const { fetchReceiptDetails } = useProfileDashboard();
  const [receiptApiDetails, setReceiptApiDetails] = useState(null);
  const receiptLoadedRef = useRef(false);

  useEffect(() => {
    if (open) setCustomerData(null);
  }, [open, order]);

  useEffect(() => {
    if (!open) {
      receiptLoadedRef.current = false;
      setReceiptApiDetails(null);
      return;
    }

    if (receiptLoadedRef.current) return;

    receiptLoadedRef.current = true;

    const loadReceiptForPrint = async () => {
      try {
        const res = await fetchReceiptDetails();

        const details = Array.isArray(res?.response)
          ? res.response[0]
          : res?.response || null;

        if (details) {
          setReceiptApiDetails(details);
        }
      } catch (error) {
        console.error("Receipt details fetch error for print:", error);
        receiptLoadedRef.current = false;
      }
    };

    loadReceiptForPrint();
  }, [open]);

  const safeJsonParse = (value) => {
    if (!value) return value;
    if (typeof value !== "string") return value;

    try {
      const parsed = JSON.parse(value);

      // Handles double-stringified JSON.
      if (typeof parsed === "string") {
        try {
          return JSON.parse(parsed);
        } catch {
          return parsed;
        }
      }

      return parsed;
    } catch {
      return value;
    }
  };

  const getRawCustomization = (item) => {
    const raw =
      item?.customization ??
      item?.customizations ??
      item?.customizationData ??
      item?.customization_details ??
      item?.customize ??
      [];

    return safeJsonParse(raw);
  };

  const getComboCustomization = (item) => {
    const directLineType = String(item?.lineType || item?.line_type || "")
      .trim()
      .toUpperCase();

    if (directLineType === "COMBO" || item?.comboId || item?.combo_id) {
      return item;
    }

    const parsed = getRawCustomization(item);

    if (
      parsed &&
      !Array.isArray(parsed) &&
      typeof parsed === "object" &&
      String(parsed?.lineType || parsed?.line_type || "")
        .trim()
        .toUpperCase() === "COMBO"
    ) {
      return parsed;
    }

    if (Array.isArray(parsed)) {
      return (
        parsed.find(
          (entry) =>
            String(entry?.lineType || entry?.line_type || "")
              .trim()
              .toUpperCase() === "COMBO",
        ) || null
      );
    }

    return null;
  };

  const parseCustomizations = (item) => {
    const parsed = getRawCustomization(item);

    // Normal customization array.
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (entry) =>
          String(entry?.lineType || entry?.line_type || "")
            .trim()
            .toUpperCase() !== "COMBO",
      );
    }

    // Combo customization is an object, not a normal customization array.
    if (getComboCustomization(item)) {
      return [];
    }

    // Single normal customization object.
    if (parsed && typeof parsed === "object") {
      return [parsed];
    }

    // Always return an array so .flatMap() is safe.
    return [];
  };

  const getCustomizationText = (item) => {
    const customizations = parseCustomizations(item);

    return customizations
      .flatMap((customization) => {
        const options =
          customization?.options ??
          customization?.option ??
          customization?.addons ??
          customization?.values ??
          customization?.value ??
          customization?.selectedOptions ??
          [];

        if (Array.isArray(options)) {
          return options;
        }

        if (typeof options === "string") {
          const parsedOptions = safeJsonParse(options);

          if (Array.isArray(parsedOptions)) {
            return parsedOptions;
          }

          return options ? [options] : [];
        }

        if (options && typeof options === "object") {
          return [options];
        }

        return [];
      })
      .map((option) => {
        if (typeof option === "string") {
          return option;
        }

        if (option && typeof option === "object") {
          return (
            option?.name ||
            option?.option_name ||
            option?.item_name ||
            option?.label ||
            option?.value ||
            ""
          );
        }

        return "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const getComboSelectedGroups = (item) => {
    const combo = getComboCustomization(item);

    return Array.isArray(combo?.selectedGroups) ? combo.selectedGroups : [];
  };

  if (!open || !order) return null;

  const displayName =
    customerData?.customerName ||
    order?.customer_name ||
    order?.customer_mobile;

  const safeAmount = (value) => {
    const num = Number(value || 0);

    if (!Number.isFinite(num)) return 0;

    return Math.max(0, num);
  };

  const roundAmount = (value) => {
    const num = Number(value || 0);

    if (!Number.isFinite(num)) return 0;

    return Math.round(num);
  };

  const getStoredDeliveryCharge = (orderId) => {
    try {
      if (!orderId) return 0;

      const raw = sessionStorage.getItem(`pos_delivery_charge_${orderId}`);
      if (!raw) return 0;

      const parsed = JSON.parse(raw);

      return safeAmount(parsed?.deliveryAmount);
    } catch (error) {
      console.error("Delivery charge cache parse error:", error);
      return 0;
    }
  };

  const normalizeOrderType = (type = "") => {
    const value = String(type).toLowerCase().trim();

    if (["home_delivery", "homedelivery", "home delivery"].includes(value)) {
      return "home_delivery";
    }

    return value;
  };

  const getApiDeliveryCharge = (order) => {
    return safeAmount(
      order?.deliveryAmount ||
        order?.delivery_amount ||
        order?.delivery_fee ||
        order?.deliveryCharges ||
        order?.delivery_charge ||
        0,
    );
  };

  const getDeliveryCharge = (order) => {
    const apiDeliveryCharge = getApiDeliveryCharge(order);

    if (apiDeliveryCharge > 0) {
      return apiDeliveryCharge;
    }

    const storedDeliveryCharge = getStoredDeliveryCharge(
      order?.orderid || order?.orderId,
    );

    if (storedDeliveryCharge > 0) {
      return storedDeliveryCharge;
    }

    const dueAmount = Number(order?.due_amount || 0);

    if (
      normalizeOrderType(order?.orderType) === "home_delivery" &&
      dueAmount < 0
    ) {
      return Math.round(Math.abs(dueAmount));
    }

    return 0;
  };

  const receiptSource = receiptApiDetails || restaurantReceiptDetails || {};

  const getExplicitGstType = (order) => {
    const value = String(
      order?.gst_type ||
        order?.gstType ||
        order?.gst_type_value ||
        receiptSource?.gst_type ||
        receiptSource?.gstType ||
        receiptSource?.tax_type ||
        receiptSource?.taxType ||
        "",
    )
      .toLowerCase()
      .trim();

    if (value === "incl" || value === "inclusive" || value === "included") {
      return "incl";
    }

    if (value === "excl" || value === "exclusive" || value === "excluded") {
      return "excl";
    }

    return "";
  };

  const isNearAmount = (a, b) => {
    return Math.abs(Number(a || 0) - Number(b || 0)) < 0.5;
  };

  const getEffectiveGstType = (order) => {
    const explicitType = getExplicitGstType(order);

    if (explicitType) return explicitType;

    const subtotal = safeAmount(order?.subtotal || order?.totalOrderPrice);
    const discount = safeAmount(order?.discount_amount);
    const otherCharges = safeAmount(order?.other_fee || order?.charges);
    const delivery = getDeliveryCharge(order);

    const cgst = safeAmount(order?.tax_cgst || order?.cgstAmount);
    const sgst = safeAmount(order?.tax_sgst || order?.sgstAmount);

    const baseAmount = subtotal - discount + otherCharges + delivery;
    const exclusiveAmount = baseAmount + cgst + sgst;

    const apiAmounts = [
      safeAmount(order?.due_amount),
      safeAmount(order?.pay_amount),
      safeAmount(order?.totalOrderPrice),
    ].filter((amount) => amount > 0);

    const hasApiExclusiveAmount = apiAmounts.some((amount) =>
      isNearAmount(amount, exclusiveAmount),
    );

    if (hasApiExclusiveAmount) return "excl";

    const hasApiInclusiveAmount = apiAmounts.some((amount) =>
      isNearAmount(amount, baseAmount),
    );

    if (hasApiInclusiveAmount) return "incl";

    if (cgst + sgst > 0) return "excl";

    return "incl";
  };

  const getGstType = getEffectiveGstType;

  const getTaxAmounts = (order) => {
    const subtotal = safeAmount(order?.subtotal || order?.totalOrderPrice);
    const discount = safeAmount(order?.discount_amount);

    const taxableAmount = Math.max(subtotal - discount, 0);

    // Hardcoded 5% inclusive GST formula
    // Example: 590 - 590 / 1.05 = 28.10 total GST
    // CGST = 14.05, SGST = 14.05
    const includedTotalTax = taxableAmount - taxableAmount / 1.05;

    return {
      cgst: includedTotalTax / 2,
      sgst: includedTotalTax / 2,
    };
  };

  const getOrderTotal = (order) => {
    const delivery = getDeliveryCharge(order);

    const subtotal = safeAmount(order?.subtotal || order?.totalOrderPrice);
    const discount = safeAmount(order?.discount_amount);

    const gstType = getGstType(order);
    const taxAmounts = getTaxAmounts(order);

    const baseAmount = subtotal - discount + delivery;

    // Inclusive: tax already included, so do not add tax again
    if (gstType === "incl") {
      return Math.max(baseAmount, 0);
    }

    // Exclusive: add CGST + SGST
    return Math.max(baseAmount + taxAmounts.cgst + taxAmounts.sgst, 0);
  };

  const taxAmounts = getTaxAmounts(order);
  const deliveryCharge = getDeliveryCharge(order);
  const displayTotal = getOrderTotal(order);
  const roundedDisplayTotal = roundAmount(displayTotal);
  const isInclusiveGst = getGstType(order) === "incl";

  const reciptDetails = {
    business_name:
      receiptSource?.business_name ||
      receiptSource?.businessName ||
      receiptSource?.organization_name ||
      receiptSource?.restaurant_name ||
      receiptSource?.shop_name ||
      order?.business_name ||
      order?.restaurant_name ||
      order?.shop_name ||
      "Restaurant",

    address:
      receiptSource?.address ||
      receiptSource?.restaurant_address ||
      receiptSource?.organization_address ||
      receiptSource?.business_address ||
      receiptSource?.shop_address ||
      receiptSource?.full_address ||
      order?.restaurant_address ||
      order?.shop_address ||
      order?.address ||
      "",

    phone_no:
      receiptSource?.phone_no ||
      receiptSource?.phoneNo ||
      receiptSource?.phone ||
      receiptSource?.mobile ||
      receiptSource?.mobile_no ||
      receiptSource?.contact_number ||
      receiptSource?.contact_no ||
      order?.restaurant_mobile ||
      order?.shop_mobile ||
      order?.phone_no ||
      "",

    gst:
      receiptSource?.gst ||
      receiptSource?.gst_no ||
      receiptSource?.gstNo ||
      receiptSource?.gst_number ||
      order?.gst_no ||
      order?.gst ||
      "",

    fssaiNo:
      receiptSource?.fssaiNo ||
      receiptSource?.fssai_no ||
      receiptSource?.fssai ||
      receiptSource?.fssai_number ||
      order?.fssai_no ||
      order?.fssaiNo ||
      "",

    taxCgst:
      receiptSource?.taxCgst ||
      receiptSource?.tax_cgst ||
      receiptSource?.cgstRate ||
      order?.tax_cgst_rate ||
      order?.cgstRate ||
      "0",

    taxSgst:
      receiptSource?.taxSgst ||
      receiptSource?.tax_sgst ||
      receiptSource?.sgstRate ||
      order?.tax_sgst_rate ||
      order?.sgstRate ||
      "0",

    tag_line1:
      receiptSource?.tag_line1 ||
      receiptSource?.tagLine1 ||
      receiptSource?.tagline1 ||
      "Visit Again",
  };

  const printOrderData = {
    ...order,

    orderid: order?.orderid || order?.orderId || "",
    daily_order_id: order?.daily_order_id || order?.dailyOrderId || "",
    orderType: order?.orderType || "",
    customer_name: displayName || "",
    customer_mobile: order?.customer_mobile || "",
    payment_status: order?.payment_status || "unpaid",
    payment_mode: order?.payment_mode || order?.paymentMode || "",
    order_date: order?.order_date || "",
    table_no: order?.table_no || order?.tableNo || "",

    items: order?.items || [],

    subtotal: Number(order?.subtotal || 0).toFixed(2),
    discount_amount: Number(order?.discount_amount || 0).toFixed(2),
    discount_percentage: order?.discount_percentage || "0",
    coupon_applied: order?.coupon_applied || false,
    coupon_code_discount: order?.coupon_code_discount || 0,

    tax_cgst: Number(order?.tax_cgst || order?.cgstAmount || 0).toFixed(2),
    tax_sgst: Number(order?.tax_sgst || order?.sgstAmount || 0).toFixed(2),

    delivery_fee: deliveryCharge.toFixed(2),

    totalOrderPrice: roundedDisplayTotal.toFixed(2),
    pay_amount: Number(order?.pay_amount || order?.paid_amount || 0).toFixed(2),
    due_amount: Number(order?.due_amount || 0).toFixed(2),

    delivery_person_name:
      order?.delivery_person_name ||
      order?.delivery_boy_name ||
      order?.delivery_partner_name ||
      "",

    delivery_person_number:
      order?.delivery_person_number ||
      order?.delivery_boy_mobile ||
      order?.delivery_partner_mobile ||
      "",

    address_json: order?.address_json || "{}",
  };

  const finalOrder = Array.isArray(order) ? order[0] : order;

  const orderStatus = String(finalOrder?.status || "")
    .trim()
    .toLowerCase();

  const deliveryStatus = String(finalOrder?.delivery_partner_status || "")
    .trim()
    .toLowerCase();

  const normalizedOrderType = String(
    finalOrder?.orderType || finalOrder?.order_type || "",
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  // Button sirf home delivery order me show hoga
  const isHomeDelivery = ["homedelivery", "home_delivery"].includes(
    normalizedOrderType,
  );

  // Delivery complete condition
  const isDelivered =
    orderStatus === "delivered" ||
    deliveryStatus === "successful" ||
    deliveryStatus === "delivered" ||
    deliveryStatus === "completed";

  // API call ke time ya delivered hone ke baad disable
  const isActionDisabled = markingComplete || isDelivered;

  // Button text
  const actionButtonLabel = markingComplete
    ? "UPDATING..."
    : isDelivered
      ? "ALREADY DELIVERED"
      : "MARK AS DELIVERED";

  return (
    <>
      <div style={{ display: "none" }}>
        <POSPrint
          cart={order?.items || []}
          totalPrice={displayTotal}
          orderType={order?.orderType}
          mobile={order?.customer_mobile}
          reciptDetails={reciptDetails}
          apiResponsePrint={printOrderData}
        />
      </div>

      {!editCustomerOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex montserrat-font-1 items-center justify-center">
          <div className="bg-white w-[900px] rounded-xl flex montserrat-font-1 overflow-hidden shadow-xl">
            {/* LEFT — Order Details */}
            <div className="flex-1 p-6 overflow-y-auto max-h-[90vh] montserrat-font-1">
              <h2 className="text-xl font-bold text-center">
                Order Details #{finalOrder?.orderid || finalOrder?.orderId}
              </h2>
              <p className="text-center text-gray-500 text-sm mb-4">
                {order.order_date || ""}
              </p>

              <hr className="mb-4" />

              {order?.orderType?.toLowerCase() === "homedelivery" && (
                <div className="mt-3 text-sm border-t border-b bg-green-200 pt-3 mb-2 rounded">
                  <p>
                    <span className="font-semibold p-2">Delivery Status:</span>{" "}
                    <span
                      className={`px-2 py-1 rounded text-xs font-bold ${
                        order.delivery_partner_status === "assigned"
                          ? "bg-green-100 text-green-600"
                          : order.delivery_partner_status === "unassigned"
                            ? "bg-yellow-100 text-yellow-600"
                            : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {(order.delivery_partner_status || "N/A").toUpperCase()}
                    </span>
                  </p>

                  <p className="mt-1 mb-2">
                    <span className="font-semibold p-2">Delivery Person:</span>{" "}
                    {order.delivery_boy_name ||
                      order.delivery_partner_name ||
                      "N/A"}
                  </p>
                </div>
              )}

              {/* CUSTOMER */}
              <div className="flex justify-between items-center mb-4 montserrat-font-1">
                <div className="text-sm">
                  <p>
                    <span className="font-semibold">Customer:</span>{" "}
                    {displayName}
                  </p>
                  <p>
                    <span className="font-semibold">Phone:</span> +91{" "}
                    {order.customer_mobile}
                  </p>
                </div>
                <button
                  onClick={() => setEditCustomerOpen(true)}
                  className="border px-3 py-1 rounded text-sm hover:bg-gray-50 transition"
                >
                  Edit Customer
                </button>
              </div>

              {/* ITEMS TABLE */}
              <table className="w-full border border-gray-300 text-sm mb-4 text-center montserrat-font-1">
                <thead>
                  <tr className="bg-gray-100 text-gray-700">
                    <th className="border px-2 py-2 w-10">#</th>
                    <th className="border px-2 py-2">Item Name</th>
                    <th className="border px-2 py-2 w-16">Qty</th>
                    <th className="border px-2 py-2 w-28">Unit Price (₹)</th>
                    <th className="border px-2 py-2 w-28">Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {order?.items?.map((item, i) => (
                    <tr key={item.id || i} className="hover:bg-gray-50">
                      <td className="border px-2 py-2">{i + 1}</td>
                      <td className="border px-2 py-2 text-left">
                        <div className="font-medium text-gray-800">
                          {getComboCustomization(item)?.comboName ||
                            item.item_name ||
                            item.productName ||
                            "Item"}{" "}
                          {!getComboCustomization(item) && item.sizeWords && (
                            <span className="text-gray-500">
                              ({item.sizeWords})
                            </span>
                          )}
                        </div>

                        {getComboCustomization(item)?.comboDescription && (
                          <div className="mt-1 text-[11px] leading-snug text-gray-500">
                            {getComboCustomization(item).comboDescription}
                          </div>
                        )}

                        {getComboSelectedGroups(item).length > 0 && (
                          <div className="mt-1.5 space-y-1">
                            {getComboSelectedGroups(item).map(
                              (group, groupIndex) => (
                                <div
                                  key={`${group?.optionGroupId || groupIndex}-${groupIndex}`}
                                  className="text-[11px] leading-snug text-gray-600"
                                >
                                  <span className="font-semibold text-gray-700">
                                    {group?.groupTitle || "Selection"}:
                                  </span>{" "}
                                  {(Array.isArray(group?.items)
                                    ? group.items
                                    : []
                                  )
                                    .map((selectedItem) => {
                                      const name =
                                        selectedItem?.productName ||
                                        selectedItem?.actualName ||
                                        selectedItem?.itemName ||
                                        "Item";

                                      const size =
                                        selectedItem?.sizeWords ||
                                        selectedItem?.variantName ||
                                        "";

                                      return size ? `${name} (${size})` : name;
                                    })
                                    .join(", ")}
                                </div>
                              ),
                            )}
                          </div>
                        )}

                        {!getComboCustomization(item) &&
                          getCustomizationText(item) && (
                            <div className="text-[11px] text-gray-500 mt-1 leading-snug">
                              Custom: {getCustomizationText(item)}
                            </div>
                          )}
                      </td>
                      <td className="border px-2 py-2">{item.quantity}</td>
                      <td className="border px-2 py-2">
                        ₹{Number(item.item_price || 0).toFixed(2)}
                      </td>
                      <td className="border px-2 py-2 font-semibold">
                        ₹{Number(item.total_price || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* SUMMARY */}
              <div className="text-sm space-y-1 montserrat-font-1">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹{Number(order.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount Amount:</span>
                  <span>₹{Number(order.discount_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other Charges:</span>
                  <span>
                    ₹{Number(order.other_fee || order.charges || 0).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>CGST {isInclusiveGst ? "(Included)" : ""}:</span>
                  <span>₹{taxAmounts.cgst.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>SGST {isInclusiveGst ? "(Included)" : ""}:</span>
                  <span>₹{taxAmounts.sgst.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Delivery Charges:</span>
                  <span>₹{deliveryCharge.toFixed(2)}</span>
                </div>
                <hr />
                <div className="flex justify-between font-bold text-lg">
                  <span>
                    Total {isInclusiveGst ? "(GST Included)" : "(GST Extra)"}:
                  </span>
                  <span>₹{roundedDisplayTotal}</span>
                </div>
              </div>

              {/* PAYMENT STATUS */}
              <p className="text-center mt-4 font-semibold">
                Payment Status:{" "}
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    order.payment_status === "paid"
                      ? "bg-green-100 text-green-600"
                      : order.payment_status === "partially_paid"
                        ? "bg-yellow-100 text-yellow-600"
                        : "bg-red-100 text-red-600"
                  }`}
                >
                  {(order.payment_status || "unpaid")
                    .replaceAll("_", " ")
                    .toUpperCase()}
                </span>
              </p>
            </div>

            {/* RIGHT — Action Buttons */}
            <div className="w-64 bg-gray-100 flex flex-col montserrat-font-1 justify-center gap-4 p-6 border-l border-dashed border-gray-300">
              <button
                onClick={() => onEdit?.(finalOrder)}
                className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold"
              >
                EDIT
              </button>
              <button
                onClick={() => onSettle?.(finalOrder)}
                className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold"
              >
                SETTLE
              </button>

              {isHomeDelivery && (
                <button
                  type="button"
                  onClick={() => {
                    if (isActionDisabled) return;

                    onMarkAsComplete?.(finalOrder);
                  }}
                  disabled={isActionDisabled}
                  className={`w-full py-4 rounded-lg font-bold transition-all active:scale-95 ${
                    isActionDisabled
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600 text-white cursor-pointer"
                  }`}
                >
                  {actionButtonLabel}
                </button>
              )}

              {/* <button className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold">
                DIRECT PRINT
              </button> */}
              <button
                onClick={() => {
                  if (!reciptDetails?.address && !reciptDetails?.phone_no) {
                    toast.error(
                      "Receipt details are still loading. Please try again.",
                    );
                    return;
                  }

                  const printBtn = document.getElementById("pos-print-btn");

                  if (printBtn) {
                    printBtn.click();
                  } else {
                    toast.error("Print component not ready");
                  }
                }}
                className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold"
              >
                PRINT
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold"
              >
                ORDER CANCEL
              </button>
              <button
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 active:scale-95 transition-all py-4 rounded-lg font-bold"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT CUSTOMER MODAL */}
      <EditCustomerModal
        open={editCustomerOpen}
        onClose={() => setEditCustomerOpen(false)}
        onCloseAll={onClose}
        order={{ ...order, ...customerData }}
        onSave={(data) => setCustomerData(data)}
      />

      <CancelOrderModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={async (reason) => {
          if (!order?.orderid) {
            toast.error("Invalid Order ID");
            return;
          }

          try {
            const success = await cancelPosOrder({
              orderId: order.orderid,
              reason,
            });

            if (success) {
              window.dispatchEvent(new Event("order-updated"));

              toast.success("Order cancelled successfully");

              setShowCancelModal(false);
              onClose();
            } else {
              toast.error("Failed to cancel order");
            }
          } catch (err) {
            console.error(err);
            toast.error("Something went wrong");
          }
        }}
      />
    </>
  );
}
